import { useAtom } from 'jotai'
import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'

import { getProfileAtom, profileAtom } from '../../../api/profile/profile'
import { colors } from '../../../theme/palette'
import { Circle, CircleIcon } from '../../ui/Circle/Circle'
import { CardColumn, Row } from '../../ui/Card/CardColumn'
import { Typography } from '../../ui/Typography/Typography'
import { baseButton } from '../../../theme/reuseables'
import {
    PersonalInfo,
    HandshakeFill,
    ChevronLeft,
    Kontigenter,
    Ok,
    Tips,
    Medlemsfordele,
    Materialer,
    Heart,
} from '../../../assets/icons'
import { L } from '../../providers/Lokalise'
import breakpoints from '../../../theme/breakpoints'
import { ImitatorNoAccessWarning } from '../../modules/Imitator/ImitatorNoAccessWarning'
import { HeaderLinks } from '../../modules/Headerlinks'
import { OrganizationWidget } from '../../modules/OrganizationWidget'

const useStyles = createUseStyles({
    container: {
        color: colors.black,
        padding: '0 1.5rem',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                gridTemplateColumns: '1fr 1fr 1fr',
                padding: '0 0 5rem',
            },
        },
    },
    profileHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '3rem 0',
    },
    nameContainer: {
        display: 'flex',
        flexBasis: '50%',
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    edit: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        ...baseButton,
        ...{
            cursor: 'pointer',
            margin: '0',
        },
    },
    section: {
        marginBottom: '3rem',

        '& > hr': {
            opacity: '0.2',
            margin: '1.25rem 0',
        },
    },
    iconWrapper: {
        position: 'relative',
        width: '90px',
        marginRight: '30px',
    },
    buttons: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        '& button': {
            margin: '10px 0',
            width: '100%',
        },
    },
    editIconWrapper: {
        top: '0',
        width: '100%',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        alignContent: 'center',
        justifyContent: 'end',
    },
    confirmButtons: {
        display: 'flex',
        zIndex: '1',
        position: 'relative',
        flexWrap: 'wrap',
        transition: 'all .3s ease-in',
        justifyContent: 'end',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        padding: '20px 0 0',
    },
    headerAreaTop: {
        display: 'flex',
        padding: '1rem 0 .5rem',
        borderBottom: `1px solid ${colors.borderColor}`,
        marginBottom: '1rem',
    },
    headerActions: {
        marginLeft: 'auto',
        flexShrink: 0,
    },
    titleContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        fontWeight: 400,
        backgroundColor: colors.white,
        borderRadius: '.5rem',
        marginBottom: '1rem',
        padding: '1rem',
    },
    titleContainerGreen: {
        display: 'flex',
        color: colors.white,
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginBottom: '1rem',
    },
    ContainerTip: {
        color: colors.white,
        justifyContent: 'flex-start',
        backgroundColor: colors.green,
        borderRadius: '.5rem',
        padding: '1rem',
    },
})

export const buildRow = (
    value: string | number,
    label: string,
    mailToLinkIcon?: JSX.Element
): Row => {
    return {
        label,
        value,
        mailToLinkIcon,
    }
}

const Organisation: React.FC = () => {
    const classes = useStyles()
    const [data, update] = useAtom(getProfileAtom)
    const [authProfile] = useAtom(profileAtom)
    const [percentageShowEdit, setPercentageShowEdit] = useState(false)
    const [percentageEditValue, setPercentageEditValue] = useState<
        number | undefined
    >(undefined)
    const [percentageOfMembersCovered, setPercentageOfMembersCovered] =
        useState(0)
    const [showImitatorModal, setShowImitatorModal] = useState(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const calcPercentageCovered = (
        numberFoaMemberUserCovers: number,
        totalNumberOfPeopleUserCovers: number
    ): number => {
        return (numberFoaMemberUserCovers / totalNumberOfPeopleUserCovers) * 100
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const calcPercentageCoveredInEditMode = (): number => {
        if (percentageEditValue < data.numberFoaMemberUserCovers || 0)
            return 100
        return (data.numberFoaMemberUserCovers / percentageEditValue) * 100
    }

    type linkItemProps = {
        linkText: string
        linkURL: string
        icon?: JSX.Element
    }
    type collapseProps = {
        buttonText: string
        icon?: JSX.Element
        links: linkItemProps[]
    }

    const LinkListItem = ({ linkText, linkURL, icon }: linkItemProps): any => {
        return (
            <a
                href={linkURL}
                target="_blank"
                className={classes.titleContainer}
                rel="noreferrer"
            >
                <Circle>
                    <CircleIcon iconWidth="20px">
                        {icon || <PersonalInfo />}
                    </CircleIcon>
                </Circle>

                <Typography tag="h3" size="M" spacing=".2rem" weight="bold">
                    {linkText}
                </Typography>
            </a>
        )
    }
    const CollapseItem = ({ buttonText, icon, links }: collapseProps): any => {
        const [show, setShow] = useState(false)
        const ref = React.useRef<HTMLDivElement | null>(null)

        const toggleCollapse = (): void => {
            if (ref.current) {
                if (show) {
                    ref.current.style.height = '0'
                } else {
                    const height = ref.current.scrollHeight
                    ref.current.style.height = `${height}`
                }
            }
            setShow(!show)
        }
        return (
            <div
                style={{
                    width: '100%',
                    marginBottom: '1rem',
                    backgroundColor: colors.white,
                    borderRadius: '.5rem',
                }}
            >
                <button
                    type="button"
                    className={classes.titleContainer}
                    style={{
                        border: 'none',
                        width: '100%',
                        marginBottom: '0',
                        cursor: 'pointer',
                    }}
                    onClick={toggleCollapse}
                >
                    <Circle>
                        <CircleIcon iconWidth="20px">
                            {icon || <PersonalInfo />}
                        </CircleIcon>
                    </Circle>
                    <Typography tag="h3" size="M" spacing=".2rem" weight="bold">
                        {buttonText}
                    </Typography>
                    <div
                        style={{
                            width: '8px',
                            height: '20px',
                            transform: show
                                ? 'rotate(270deg)'
                                : 'rotate(90deg)',
                            transformOrigin: 'center',
                            marginLeft: 'auto',
                            marginRight: '0.5rem',
                            transition: 'all 0.2s linear',
                        }}
                    >
                        <ChevronLeft />
                    </div>
                </button>
                <div
                    ref={ref}
                    style={{
                        overflow: 'hidden',
                        height: show ? ref.current.scrollHeight : '0',
                        transition: 'height 0.2s linear',
                        backgroundColor: colors.white,
                        borderRadius: '.5rem',
                    }}
                >
                    <hr
                        style={{
                            borderColor: colors.gray500,
                            marginInline: '1rem',
                        }}
                    />

                    {links.map((link) => {
                        return (
                            <a
                                href={link.linkURL}
                                target="_blank"
                                style={{
                                    padding: '1rem',
                                    marginBottom: '1rem',
                                    display: 'block',
                                }}
                                rel="noreferrer"
                            >
                                <Typography
                                    tag="h3"
                                    size="S"
                                    spacing=".2rem"
                                    weight="bold"
                                >
                                    {link.linkText}
                                </Typography>
                            </a>
                        )
                    })}
                </div>
            </div>
        )
    }
    useEffect(() => {
        setPercentageOfMembersCovered(
            data.totalNumberOfPeopleUserCovers !== null
                ? calcPercentageCovered(
                      data.numberFoaMemberUserCovers,
                      data.totalNumberOfPeopleUserCovers
                  )
                : 0
        )
        setPercentageShowEdit(false)

        setPercentageEditValue(undefined)
    }, [data])

    useEffect(() => {
        if (percentageShowEdit) {
            setPercentageOfMembersCovered(calcPercentageCoveredInEditMode())
        }
    }, [
        calcPercentageCovered,
        calcPercentageCoveredInEditMode,
        percentageOfMembersCovered,
        percentageShowEdit,
    ])

    if (!data) return null

    return (
        <div className={`${classes.container} page-up`}>
            <div className={`${classes.headerAreaTop} desktop-only`}>
                <div className={classes.headerActions}>
                    <HeaderLinks showGotoMembrsearchModal={false} />
                </div>
            </div>
            <div className={classes.profileHeader}>
                <div className={classes.nameContainer}>
                    <Typography tag="h1" size="XXXXL" spacing=".2rem">
                        {L('organisering_titel')}
                    </Typography>
                </div>
            </div>
            <div className={classes.nameContainer}>
                <Typography tag="p" size="S" color={colors.gray900}>
                    {L('organisering_subtitel')}
                </Typography>
            </div>
            <div className={classes.section}>
                <hr />
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        gridGap: '1rem',
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            maxWidth: '100%',
                            // background: 'white',
                        }}
                    >
                        <LinkListItem
                            linkText={L('organisering_link_1_text')}
                            linkURL={L('organisering_link_1_url')}
                            icon={<Kontigenter />}
                        />
                        <CollapseItem
                            buttonText={L('organisering_button_2_text')}
                            links={[
                                {
                                    linkText: L(
                                        'organisering_button_2_sublink_1_text'
                                    ),
                                    linkURL: L(
                                        'organisering_button_2_sublink_1_url'
                                    ),
                                },
                                {
                                    linkText: L(
                                        'organisering_button_2_sublink_2_text'
                                    ),
                                    linkURL: L(
                                        'organisering_button_2_sublink_2_url'
                                    ),
                                },
                            ]}
                            icon={<Ok />}
                        />
                        <LinkListItem
                            linkText={L('organisering_link_3_text')}
                            linkURL={L('organisering_link_3_url')}
                            icon={<Medlemsfordele />}
                        />
                        <CollapseItem
                            buttonText={L('organisering_button_4_text')}
                            icon={<Materialer />}
                            links={[
                                {
                                    linkText: L(
                                        'organisering_button_4_sublink_1_text'
                                    ),
                                    linkURL: L(
                                        'organisering_button_4_sublink_1_url'
                                    ),
                                },
                                {
                                    linkText: L(
                                        'organisering_button_4_sublink_2_text'
                                    ),
                                    linkURL: L(
                                        'organisering_button_4_sublink_2_url'
                                    ),
                                },
                                {
                                    linkText: L(
                                        'organisering_button_4_sublink_3_text'
                                    ),
                                    linkURL: L(
                                        'organisering_button_4_sublink_3_url'
                                    ),
                                },
                                {
                                    linkText: L(
                                        'organisering_button_4_sublink_4_text'
                                    ),
                                    linkURL: L(
                                        'organisering_button_4_sublink_4_url'
                                    ),
                                },
                            ]}
                        />
                        <LinkListItem
                            linkText={L('organisering_link_5_text')}
                            linkURL={L('organisering_link_5_url')}
                            icon={<Heart />}
                        />
                    </div>
                    <CardColumn
                        titleIcon={<HandshakeFill />}
                        titleLabel={L(
                            'TRPROFIL_ORGANISERINGSWIDGET_OVERSKRIFT'
                        )}
                        rows={[
                            {
                                label: '',
                                value: '',
                                useContent: true,
                                content: (
                                    <OrganizationWidget
                                        data={{
                                            numberFoaMemberUserCoversTR:
                                                data.numberFoaMemberUserCoversTR,
                                            numberFoaMemberUserCoversFTR:
                                                data.numberFoaMemberUserCoversFTR,
                                            totalNumberOfPeopleUserCoversTR:
                                                data.totalNumberOfPeopleUserCoversTR,
                                            totalNumberOfPeopleUserCoversFTR:
                                                data.totalNumberOfPeopleUserCoversFTR,
                                        }}
                                        authProfile={authProfile}
                                        onUpdate={() =>
                                            update({ type: 'refetch' })
                                        }
                                        onImitatorModal={() =>
                                            setShowImitatorModal(true)
                                        }
                                    />
                                ),
                            },
                        ]}
                    />
                    <div
                        style={{ gridColumn: 2 }}
                        className={classes.ContainerTip}
                    >
                        <div className={classes.titleContainerGreen}>
                            <Circle mWidth="2rem">
                                <CircleIcon backgroundColor={colors.white}>
                                    <Tips />
                                </CircleIcon>
                            </Circle>

                            <Typography
                                tag="h3"
                                size="S"
                                spacing=".2rem"
                                color={colors.white}
                            >
                                {L('organisering_tips_titel')}
                            </Typography>
                        </div>
                        <Typography
                            tag="p"
                            size="XS"
                            style={{
                                fontStyle: 'italic',
                                whiteSpace: 'pre-wrap',
                            }}
                            spacing=".2rem"
                            color={colors.white}
                        >
                            {L('organisering_tips_text')}
                        </Typography>
                    </div>
                </div>
            </div>
            <ImitatorNoAccessWarning
                show={showImitatorModal}
                onClose={() => setShowImitatorModal(false)}
            />
        </div>
    )
}

export { Organisation }
